import * as ExcelJS from "exceljs";

/*
  Reading an uploaded Excel or CSV file in the browser.

  The server never sees the file - it is turned into plain rows here and those
  are what get posted. That keeps spreadsheet parsing off a shared host, where
  adding a library means shipping a whole vendor tree, and it lets the council
  see exactly what will be imported before anything is written.

  The first row is the header. Header names are lowercased and spaces turned
  into underscores, so "Roll Number", "roll number" and "roll_number" all
  arrive as roll_number.
*/

const normalise = (value) =>
  String(value === null || value === undefined ? "" : value)
    .trim()
    .toLowerCase()
    .replace(/[\s.-]+/g, "_");

/** A cell can hold a formula result, a rich-text run, or a date. */
const cellText = (value) => {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }
  if (typeof value === "object") {
    if (value.text !== undefined) return String(value.text);
    if (value.result !== undefined) return String(value.result);
    if (Array.isArray(value.richText)) {
      return value.richText.map((r) => r.text).join("");
    }
    if (value.hyperlink !== undefined) return String(value.text || value.hyperlink);
    return "";
  }
  return String(value);
};

/** Split one CSV line, honouring "quoted, fields" and "" escapes. */
const splitCsvLine = (line) => {
  const out = [];
  let field = "";
  let quoted = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    if (quoted) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          quoted = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ",") {
      out.push(field);
      field = "";
    } else {
      field += ch;
    }
  }

  out.push(field);
  return out;
};

const rowsFromGrid = (grid) => {
  if (!grid.length) return { headers: [], rows: [] };

  const headers = grid[0].map(normalise);

  const rows = grid
    .slice(1)
    .map((cells) => {
      const row = {};
      headers.forEach((key, i) => {
        if (key) row[key] = String(cells[i] === undefined ? "" : cells[i]).trim();
      });
      return row;
    })
    // A spreadsheet nearly always carries a tail of blank rows.
    .filter((row) => Object.values(row).some((v) => v !== ""));

  return { headers, rows };
};

const readCsv = (text) => {
  const grid = text
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n")
    .filter((line) => line.trim() !== "")
    .map(splitCsvLine);

  return rowsFromGrid(grid);
};

const readXlsx = async (buffer) => {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buffer);

  const ws = wb.worksheets[0];
  if (!ws) return { headers: [], rows: [] };

  const grid = [];
  ws.eachRow({ includeEmpty: false }, (row) => {
    const cells = [];
    // values is 1-based, with index 0 unused.
    const values = row.values || [];
    for (let i = 1; i < values.length; i++) {
      cells.push(cellText(values[i]));
    }
    grid.push(cells);
  });

  return rowsFromGrid(grid);
};

/**
 * @param {File} file  .xlsx, .xls or .csv
 * @returns {Promise<{headers: string[], rows: object[]}>}
 */
export const readSheet = async (file) => {
  const name = (file.name || "").toLowerCase();

  if (name.endsWith(".csv") || file.type === "text/csv") {
    return readCsv(await file.text());
  }

  return readXlsx(await file.arrayBuffer());
};
